// src/components/PageConfigurator.tsx

import React from "react";
import HeroSection from "./common/HeroSection";
//import TrustedBySection from "./common/TrustedBySection";
import FeatureSection from "./common/FeatureSection";
import BlogPreview from "./common/BlogPreview";
import CallToAction from "./common/CallToAction";
import UserReviews from "./common/UserReviews";
import AppIntegrations from "./common/AppIntegrations";
//import PricingTiers from "./common/PricingTiers";
import FeatureComparison from "./common/FeatureComparison";
import FAQ from "./common/FAQ";
import ContactForm from "./common/ContactForm";
import ContactInfo from "./common/ContactInfo";
import BlogPost from "./common/BlogPost";
import MissionSection from "./common/MissionSection";
//import TeamSection from "./common/TeamSection";
import TextSection from "./common/TextSection"; // Import TextSection
import ServiceCatalogSection from "./common/ServiceCatalogSection";
import MatchXREventSection from "./common/MatchXREventSection";
import {
  HomePageData,
  PricingPageData,
  FAQPageData,
  ContactPageData,
  BlogPageData,
  ProductPageData,
  AboutPageData,
  LegalPageData, // Import LegalPageData
  HeroSectionData,
  FeatureSectionData,
  BlogPreviewData,
  CallToActionData,
  UserReviewsData,
  AppIntegrationsData,
  FeatureComparisonData,
  FAQData,
  ContactInfoData,
  BlogPostData,
  TextSectionData,
  ServiceCatalogSectionData,
  SolutionDetailsPageData,
  SolutionPageData, // Import TextSectionData
} from "../types/interfaces";
import "../styles/PageConfigurator.css";
import "../styles/ContactPage.css";
import SolutionListSection from "./common/SolutionListSection";
import { useContactSettings } from "../data/ContactSettingsContext";
import { contactSettingsToLinks } from "../data/contactSettings";

interface PageConfiguratorProps {
  pageData:
    | HomePageData
    | PricingPageData
    | FAQPageData
    | ContactPageData
    | BlogPageData
    | ProductPageData
    | SolutionPageData
    | AboutPageData
    | LegalPageData // Add LegalPageData,
    | SolutionDetailsPageData; // Add SolutionDetailsPageData
    
  pageName: string;
}

const PageConfigurator: React.FC<PageConfiguratorProps> = ({
  pageData,
  pageName,
}) => {
  const commonPageStyles = "page";
  const { settings: contactSettings } = useContactSettings();

  // General Pages (home, pricing, blog, product)
  if (
    pageName !== "contact" &&
    pageName !== "about" &&
    pageName !== "privacy" &&
    pageName !== "terms" &&
    "sections" in pageData
  ) {
    const requestServiceName =
      pageName === "solution-details"
        ? (
            (pageData as SolutionDetailsPageData).sections.find(
              (section) => section.type === "HeroSection"
            ) as HeroSectionData | undefined
          )?.title
        : undefined;

    return (
      <div className={`${commonPageStyles} ${pageName}`}>
        {pageData.sections.map((section, index) => {

          const sectionId = `section-${index + 1}`; // Create a unique id for each section

          switch (section.type) {
            case "HeroSection":
              return (
                <HeroSection
                key={index}
                {...(section as HeroSectionData)}
                id={section.id||sectionId}
                ctaLink={(section as HeroSectionData).ctaLink} // Pass ctaLink from data
                requestServiceName={requestServiceName}

              />
              );
            // case "TrustedBySection":
            //   return (
            //     <TrustedBySection
            //       key={index}
            //       {...(section as TrustedBySectionData)}
            //     />
            //   );
            case "FeatureSection":
              return (
                <FeatureSection
                id={section.id || sectionId}
                key={index}
                  {...(section as FeatureSectionData)}
                pageType={pageName}
                requestServiceName={requestServiceName}
                />
              );
            case "BlogPreview":
              return (
                <BlogPreview key={index} {...(section as BlogPreviewData)} />
              );
            case "CallToAction":
              return (
                <CallToAction
                  key={index}
                  {...(section as CallToActionData)}
                  requestServiceName={requestServiceName}
                />
              );
            case "UserReviews":
              return (
                <UserReviews key={index} {...(section as UserReviewsData)} />
              );
            case "AppIntegrations":
              return (
                <AppIntegrations
                  key={index}
                  {...(section as AppIntegrationsData)}
                  
                />
              );
            // case "PricingTiers":
            //   return (
            //     <PricingTiers key={index} {...(section as PricingTiersData)} />
            //   );
            case "FeatureComparison":
              return (
                <FeatureComparison
                  key={index}
                  {...(section as FeatureComparisonData)}
                />
              );
            case "FAQ":
              return <FAQ
              key={index}
              {...(section as FAQData)}
              id={section.id || `section-${index + 1}`}
            />;
            case "BlogPost":
              return <BlogPost key={index} {...(section as BlogPostData)} />;
            case "TextSection": // Handle TextSection
              return (
                <TextSection key={index} {...(section as TextSectionData)} />
              );
            case "ServiceCatalogSection":
              return (
                <ServiceCatalogSection
                  key={index}
                  {...(section as ServiceCatalogSectionData)}
                />
              );
            case "MatchXREventSection":
              return <MatchXREventSection key={index} />;
            case "SolutionListSection":
              return <SolutionListSection key={index} />;
            default:
              return null;
          }
        })}
      </div>
    );
  }

  // Contact Page
  if (pageName === "contact" && "sections" in pageData) {
    const contactData = pageData as ContactPageData;
    const info = contactData.sections.find(
      (section): section is ContactInfoData => section.type === "ContactInfo"
    );

    if (!info) return null;

    return (
      <main className={`${commonPageStyles} ${pageName}`}>
        <section className="contact-hero" aria-labelledby="contact-page-title">
          <div className="contact-hero-inner">
            <div className="contact-hero-copy">
              <h1 id="contact-page-title">{contactSettings.heroTitle}</h1>
              <p>{contactSettings.heroDescription}</p>
              <div className="contact-hero-actions">
                <a className="contact-hero-primary" href={`mailto:${contactSettings.email}`}>
                  Email AlluraTech <span aria-hidden="true">↗</span>
                </a>
                <a className="contact-hero-secondary" href={contactSettings.whatsappCommunityUrl} target="_blank" rel="noopener noreferrer">
                  Join WhatsApp community <span aria-hidden="true">↗</span>
                </a>
              </div>
            </div>
            <div className="contact-hero-art" aria-hidden="true">
              <div className="contact-hero-orbit contact-hero-orbit-one" />
              <div className="contact-hero-orbit contact-hero-orbit-two" />
              <img src="/logo512.png" alt="" />
            </div>
          </div>
        </section>
        <section className="contact-main" aria-label="Contact options">
          <div className="contact-main-inner">
            <ContactForm contactEmail={contactSettings.email} />
            <ContactInfo address={contactSettings.locationLabel} email={contactSettings.email} socialLinks={contactSettingsToLinks(contactSettings)} />
          </div>
        </section>
      </main>
    );
  }


  // About Page
  if (pageName === "about") {
    if (
      "hero" in pageData &&
      "mission" in pageData &&
      "team" in pageData &&
      "callToAction" in pageData
    ) {
      const aboutData = pageData as AboutPageData;
      return (
        <div className={`${commonPageStyles} ${pageName}`}>
          <HeroSection id="about-hero" {...aboutData.hero} />
          <MissionSection {...aboutData.mission} />
          {/* <TeamSection team={aboutData.team} /> */}
          <CallToAction {...aboutData.callToAction} />
        </div>
      );
    }
  }

  // Privacy and Terms Pages
  if (pageName === "privacy" || pageName === "terms") {
    if ("sections" in pageData) {
      const legalData = pageData as LegalPageData;
      return (
        <div className={`${commonPageStyles} ${pageName}`}>
          {legalData.sections.map((section, index) => {
           const sectionId = `${pageName}-section-${index + 1}`; // Create a unique id based on the index

            switch (section.type) {
              case "HeroSection":
                return (
                  <HeroSection key={index} {...(section as HeroSectionData)} id={sectionId}/>
                );
              case "TextSection":
                return (
                  <TextSection key={index} {...(section as TextSectionData)} />
                );
              case "CallToAction":
                return (
                  <CallToAction
                    key={index}
                    {...(section as CallToActionData)}
                  />
                );
              default:
                return null;
            }
          })}
        </div>
      );
    }
  }

  // Fallback
  return null;
};

export default PageConfigurator;
